import { type ReactNode, useId } from 'react';
import { useReducedMotion } from '@/ui';

/**
 * Simple phone and window mockups for the install gate, drawn in the brand style: flat pastel
 * fills, thick ink outlines, a hard offset shadow, no gradients and no blur. Text is drawn as
 * bars on purpose (at this size words are unreadable, and the caption beside each mockup says
 * what to tap). The thing to tap sits on a flat peach disc with a pulsing grape ring; the ring
 * stands still under reduced motion.
 *
 * Phones are 120 x 224 in their own coordinates, desktop windows 220 x 150.
 */

export type Scene =
  | 'ios-bar'
  | 'ios-share'
  | 'ios-sheet'
  | 'ios-add'
  | 'home'
  | 'android-bar'
  | 'android-menu'
  | 'samsung-bar'
  | 'samsung-menu'
  | 'desktop-bar'
  | 'desktop-dialog'
  | 'desktop-window'
  | 'mac-menu';

const INK = 'var(--color-ink)';
const C = {
  lilac: 'var(--color-lilac)',
  pale: 'var(--color-lilac-pale)',
  grape: 'var(--color-grape)',
  lavender: 'var(--color-lavender)',
  sky: 'var(--color-sky)',
  mint: 'var(--color-mint)',
  butter: 'var(--color-butter)',
  peach: 'var(--color-peach)',
  white: 'var(--color-white)',
};

/** Phones show 224 tall, desktop windows 150 tall; the aspect ratio is part of each scene. */
export const SCENE_SIZE: Record<Scene, { w: number; h: number }> = {
  'ios-bar': { w: 120, h: 224 },
  'ios-share': { w: 120, h: 224 },
  'ios-sheet': { w: 120, h: 224 },
  'ios-add': { w: 120, h: 224 },
  home: { w: 120, h: 224 },
  'android-bar': { w: 120, h: 224 },
  'android-menu': { w: 120, h: 224 },
  'samsung-bar': { w: 120, h: 224 },
  'samsung-menu': { w: 120, h: 224 },
  'desktop-bar': { w: 220, h: 150 },
  'desktop-dialog': { w: 220, h: 150 },
  'desktop-window': { w: 220, h: 150 },
  'mac-menu': { w: 220, h: 150 },
};

function Bars({ x, y, widths, gap = 8, h = 4, fill = INK, opacity = 0.25 }: BarsProps) {
  return (
    <g opacity={opacity}>
      {widths.map((w, i) => (
        <rect key={`${y}-${i.toString()}`} x={x} y={y + i * gap} width={w} height={h} rx={2} fill={fill} />
      ))}
    </g>
  );
}
interface BarsProps {
  x: number;
  y: number;
  widths: number[];
  gap?: number;
  h?: number;
  fill?: string;
  opacity?: number;
}

/** The spot to tap: a flat peach disc, an ink outline, and a pulsing grape ring around it. */
function Tap({ cx, cy, r = 10, children }: { cx: number; cy: number; r?: number; children?: ReactNode }) {
  const still = useReducedMotion();
  return (
    <g>
      <circle cx={cx} cy={cy} r={r + 4} fill="none" stroke={C.grape} strokeWidth={2.5}>
        {!still && (
          <>
            <animate attributeName="r" values={`${r + 3};${r + 7};${r + 3}`} dur="1.6s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="1;0.35;1" dur="1.6s" repeatCount="indefinite" />
          </>
        )}
      </circle>
      <circle cx={cx} cy={cy} r={r} fill={C.peach} stroke={INK} strokeWidth={3} />
      {children}
    </g>
  );
}

const Dots3 = ({ cx, cy }: { cx: number; cy: number }) => (
  <g fill={INK}>
    {[-4.5, 0, 4.5].map((dx) => (
      <circle key={dx} cx={cx + dx} cy={cy} r={1.9} />
    ))}
  </g>
);

const Kebab = ({ cx, cy }: { cx: number; cy: number }) => (
  <g fill={INK}>
    {[-4.5, 0, 4.5].map((dy) => (
      <circle key={dy} cx={cx} cy={cy + dy} r={1.9} />
    ))}
  </g>
);

const Burger = ({ cx, cy }: { cx: number; cy: number }) => (
  <g stroke={INK} strokeWidth={2} strokeLinecap="round">
    {[-4.5, 0, 4.5].map((dy) => (
      <line key={dy} x1={cx - 5} x2={cx + 5} y1={cy + dy} y2={cy + dy} />
    ))}
  </g>
);

/** Share glyph: a box with an arrow leaving it. */
const ShareGlyph = ({ cx, cy, s = 1 }: { cx: number; cy: number; s?: number }) => (
  <g
    transform={`translate(${cx} ${cy}) scale(${s})`}
    fill="none"
    stroke={INK}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M-5 -1 V6 H5 V-1" />
    <path d="M0 4 V-8 M-3.5 -4.5 L0 -8 L3.5 -4.5" />
  </g>
);

const PlusSquare = ({ x, y, s = 14 }: { x: number; y: number; s?: number }) => (
  <g>
    <rect x={x} y={y} width={s} height={s} rx={3} fill={C.white} stroke={INK} strokeWidth={2} />
    <path
      d={`M${x + s / 2} ${y + 3.5} V${y + s - 3.5} M${x + 3.5} ${y + s / 2} H${x + s - 3.5}`}
      stroke={INK}
      strokeWidth={2}
      strokeLinecap="round"
    />
  </g>
);

/** A phone: hard grape shadow, white body, thick ink outline, screen clipped inside. */
function PhoneFrame({ id, children }: { id: string; children: ReactNode }) {
  return (
    <>
      <defs>
        <clipPath id={`${id}-screen`}>
          <rect x={9} y={8} width={102} height={208} rx={12} />
        </clipPath>
      </defs>
      <rect x={10} y={9} width={110} height={214} rx={16} fill={C.grape} />
      <rect x={4} y={3} width={110} height={214} rx={16} fill={C.white} stroke={INK} strokeWidth={4} />
      <g clipPath={`url(#${id}-screen)`}>{children}</g>
      <rect x={46} y={12} width={26} height={6} rx={3} fill={INK} />
    </>
  );
}

/** A desktop browser window with the same shadow and outline. */
function WindowFrame({ children, title = false }: { children: ReactNode; title?: boolean }) {
  return (
    <>
      <rect x={9} y={9} width={206} height={136} rx={8} fill={C.grape} />
      <rect x={3} y={3} width={206} height={136} rx={8} fill={C.white} stroke={INK} strokeWidth={4} />
      <rect x={5} y={5} width={202} height={title ? 22 : 30} rx={5} fill={C.pale} />
      <line x1={5} x2={207} y1={title ? 27 : 35} y2={title ? 27 : 35} stroke={INK} strokeWidth={2} />
      {[14, 24, 34].map((x, i) => (
        <circle key={x} cx={x} cy={16} r={3.2} fill={[C.peach, C.butter, C.mint][i]} stroke={INK} strokeWidth={1.5} />
      ))}
      {children}
    </>
  );
}

const PageBody = ({ top = 38 }: { top?: number }) => (
  <g>
    <rect x={14} y={top + 6} width={80} height={14} rx={3} fill={C.lavender} stroke={INK} strokeWidth={2} />
    <Bars x={14} y={top + 28} widths={[170, 150, 120]} gap={9} />
  </g>
);

function Scene({ scene, id }: { scene: Scene; id: string }) {
  switch (scene) {
    case 'ios-bar':
      return (
        <PhoneFrame id={id}>
          <rect x={9} y={20} width={102} height={40} fill={C.lilac} />
          <rect x={20} y={32} width={56} height={14} rx={3} fill={C.white} stroke={INK} strokeWidth={2} />
          <Bars x={18} y={74} widths={[84, 70, 76, 50]} gap={11} h={5} />
          <rect x={9} y={178} width={102} height={38} fill={C.pale} />
          <line x1={9} x2={111} y1={178} y2={178} stroke={INK} strokeWidth={2} />
          <rect x={17} y={187} width={64} height={18} rx={9} fill={C.white} stroke={INK} strokeWidth={2} />
          <Bars x={27} y={194} widths={[34]} h={4} />
          <Tap cx={96} cy={196} r={11}>
            <Dots3 cx={96} cy={196} />
          </Tap>
        </PhoneFrame>
      );
    case 'ios-share':
      return (
        <PhoneFrame id={id}>
          <rect x={9} y={20} width={102} height={70} fill={C.lilac} />
          <rect x={9} y={92} width={102} height={124} fill={C.white} />
          <line x1={9} x2={111} y1={92} y2={92} stroke={INK} strokeWidth={3} />
          <rect x={20} y={102} width={80} height={16} rx={4} fill={C.pale} stroke={INK} strokeWidth={2} />
          <rect x={20} y={124} width={80} height={22} rx={4} fill={C.butter} stroke={INK} strokeWidth={3} />
          <PlusSquare x={26} y={128} />
          <Bars x={46} y={133} widths={[44]} h={5} opacity={0.55} />
          <rect
            x={20}
            y={152}
            width={80}
            height={16}
            rx={4}
            fill={C.white}
            stroke={INK}
            strokeWidth={2}
            opacity={0.6}
          />
          <Bars x={26} y={158} widths={[50]} opacity={0.18} />
          <Tap cx={98} cy={135} r={8}>
            <ShareGlyph cx={98} cy={135} s={0.7} />
          </Tap>
        </PhoneFrame>
      );
    case 'ios-sheet':
      return (
        <PhoneFrame id={id}>
          <rect x={9} y={20} width={102} height={196} fill={C.lilac} />
          <rect x={9} y={66} width={102} height={150} rx={10} fill={C.white} stroke={INK} strokeWidth={3} />
          <rect x={20} y={76} width={80} height={16} rx={4} fill={C.pale} stroke={INK} strokeWidth={2} />
          <rect x={20} y={98} width={80} height={16} rx={4} fill={C.white} stroke={INK} strokeWidth={2} opacity={0.6} />
          <rect x={18} y={120} width={84} height={26} rx={5} fill={C.butter} stroke={INK} strokeWidth={3} />
          <PlusSquare x={24} y={126} s={14} />
          <Bars x={44} y={129} widths={[50, 34]} gap={8} h={4} opacity={0.5} />
          <rect
            x={20}
            y={156}
            width={80}
            height={14}
            rx={4}
            fill={C.white}
            stroke={INK}
            strokeWidth={2}
            opacity={0.5}
          />
          <rect x={34} y={186} width={52} height={20} rx={10} fill={C.pale} stroke={INK} strokeWidth={2} />
          <Bars x={44} y={194} widths={[32]} opacity={0.4} />
        </PhoneFrame>
      );
    case 'ios-add':
      return (
        <PhoneFrame id={id}>
          <rect x={9} y={20} width={102} height={196} fill={C.lilac} />
          <rect x={9} y={36} width={102} height={180} rx={10} fill={C.white} stroke={INK} strokeWidth={3} />
          <Bars x={18} y={48} widths={[24]} h={5} opacity={0.4} />
          <rect x={78} y={42} width={28} height={16} rx={8} fill={C.mint} stroke={INK} strokeWidth={2.5} />
          <Bars x={86} y={48} widths={[12]} h={4} opacity={0.6} />
          <rect x={20} y={70} width={30} height={30} rx={7} fill={C.lilac} stroke={INK} strokeWidth={2.5} />
          <image href="/icons/icon-192.png" x={22} y={72} width={26} height={26} />
          <rect x={58} y={74} width={44} height={22} rx={4} fill={C.pale} stroke={INK} strokeWidth={2} />
          <Bars x={62} y={82} widths={[30]} opacity={0.5} />
          <rect x={18} y={112} width={84} height={34} rx={6} fill={C.pale} stroke={INK} strokeWidth={2} />
          <Bars x={24} y={120} widths={[58]} gap={9} />
          <rect x={18} y={154} width={84} height={30} rx={6} fill={C.white} stroke={INK} strokeWidth={2} />
          <Bars x={24} y={166} widths={[40]} opacity={0.5} />
          <Tap cx={84} cy={169} r={14} />
          <rect x={72} y={162} width={24} height={14} rx={7} fill={C.mint} stroke={INK} strokeWidth={2.5} />
          <circle cx={90} cy={169} r={5} fill={C.white} stroke={INK} strokeWidth={2} />
        </PhoneFrame>
      );
    case 'home':
      return (
        <PhoneFrame id={id}>
          <rect x={9} y={20} width={102} height={196} fill={C.lilac} />
          {[0, 1, 2, 3].flatMap((row) =>
            [0, 1, 2, 3].map((col) => {
              const x = 15 + col * 24;
              const y = 36 + row * 30;
              const tone = [C.sky, C.mint, C.butter, C.lavender, C.white, C.pale][(row * 4 + col) % 6];
              const ours = row === 1 && col === 2;
              if (ours) return null;
              return (
                <rect
                  key={`${row}-${col}`}
                  x={x}
                  y={y}
                  width={18}
                  height={18}
                  rx={5}
                  fill={tone}
                  stroke={INK}
                  strokeWidth={2}
                />
              );
            }),
          )}
          <Tap cx={72 + 9} cy={66 + 9} r={17} />
          <rect x={72} y={66} width={18} height={18} rx={5} fill={C.lilac} stroke={INK} strokeWidth={2} />
          <image href="/icons/icon-192.png" x={72} y={66} width={18} height={18} />
          <rect x={14} y={188} width={92} height={22} rx={10} fill={C.pale} stroke={INK} strokeWidth={2} />
        </PhoneFrame>
      );
    case 'android-bar':
      return (
        <PhoneFrame id={id}>
          <rect x={9} y={20} width={102} height={30} fill={C.pale} />
          <line x1={9} x2={111} y1={50} y2={50} stroke={INK} strokeWidth={2} />
          <rect x={15} y={27} width={62} height={16} rx={8} fill={C.white} stroke={INK} strokeWidth={2} />
          <Bars x={24} y={33} widths={[36]} opacity={0.4} />
          <rect x={15} y={64} width={80} height={26} rx={4} fill={C.lavender} stroke={INK} strokeWidth={2.5} />
          <Bars x={15} y={104} widths={[84, 70, 76, 50]} gap={11} h={5} />
          <Tap cx={96} cy={35} r={11}>
            <Kebab cx={96} cy={35} />
          </Tap>
        </PhoneFrame>
      );
    case 'android-menu':
      return (
        <PhoneFrame id={id}>
          <rect x={9} y={20} width={102} height={30} fill={C.pale} />
          <line x1={9} x2={111} y1={50} y2={50} stroke={INK} strokeWidth={2} />
          <rect x={30} y={34} width={76} height={150} rx={6} fill={C.white} stroke={INK} strokeWidth={3} />
          {[0, 1].map((i) => (
            <Bars key={i} x={40} y={46 + i * 22} widths={[50]} h={5} opacity={0.25} />
          ))}
          <rect x={34} y={86} width={68} height={24} rx={4} fill={C.butter} stroke={INK} strokeWidth={3} />
          <rect x={40} y={91} width={10} height={14} rx={2} fill={C.white} stroke={INK} strokeWidth={2} />
          <Bars x={56} y={95} widths={[38]} h={5} opacity={0.55} />
          {[0, 1, 2].map((i) => (
            <Bars key={i} x={40} y={124 + i * 20} widths={[50]} h={5} opacity={0.25} />
          ))}
        </PhoneFrame>
      );
    case 'samsung-bar':
      return (
        <PhoneFrame id={id}>
          <rect x={9} y={20} width={102} height={40} fill={C.lilac} />
          <Bars x={18} y={74} widths={[84, 70, 76, 50]} gap={11} h={5} />
          <rect x={9} y={176} width={102} height={40} fill={C.pale} />
          <line x1={9} x2={111} y1={176} y2={176} stroke={INK} strokeWidth={2} />
          <path d="M20 196 l6 -5 v10z" fill={INK} opacity={0.5} />
          <rect x={34} y={186} width={40} height={20} rx={10} fill={C.white} stroke={INK} strokeWidth={2} />
          <Tap cx={96} cy={196} r={11}>
            <Burger cx={96} cy={196} />
          </Tap>
        </PhoneFrame>
      );
    case 'samsung-menu':
      return (
        <PhoneFrame id={id}>
          <rect x={9} y={20} width={102} height={196} fill={C.lilac} />
          <rect x={9} y={104} width={102} height={112} rx={10} fill={C.white} stroke={INK} strokeWidth={3} />
          {[0, 1, 2].flatMap((row) =>
            [0, 1, 2].map((col) => {
              const x = 17 + col * 30;
              const y = 116 + row * 32;
              const ours = row === 0 && col === 1;
              return (
                <g key={`${row}-${col}`}>
                  <rect
                    x={x}
                    y={y}
                    width={24}
                    height={24}
                    rx={6}
                    fill={ours ? C.butter : C.pale}
                    stroke={INK}
                    strokeWidth={ours ? 3 : 2}
                  />
                  <Bars x={x + 4} y={y + 28} widths={[16]} h={3} opacity={0.3} />
                </g>
              );
            }),
          )}
          <Tap cx={61} cy={128} r={15} />
          <rect x={49} y={116} width={24} height={24} rx={6} fill={C.butter} stroke={INK} strokeWidth={3} />
          <PlusSquare x={54} y={121} s={14} />
        </PhoneFrame>
      );
    case 'desktop-bar':
      return (
        <WindowFrame>
          <rect x={46} y={11} width={120} height={16} rx={8} fill={C.white} stroke={INK} strokeWidth={2} />
          <Bars x={58} y={17} widths={[56]} opacity={0.35} />
          <Tap cx={186} cy={19} r={9}>
            <rect x={181} y={14} width={10} height={8} rx={1.5} fill="none" stroke={INK} strokeWidth={1.8} />
            <path
              d="M186 15.5 V20.5 M183.8 18.5 L186 20.7 L188.2 18.5"
              stroke={INK}
              strokeWidth={1.6}
              fill="none"
              strokeLinecap="round"
            />
          </Tap>
          <PageBody />
        </WindowFrame>
      );
    case 'desktop-dialog':
      return (
        <WindowFrame>
          <rect x={46} y={11} width={120} height={16} rx={8} fill={C.white} stroke={INK} strokeWidth={2} />
          <Bars x={58} y={17} widths={[56]} opacity={0.35} />
          <PageBody />
          <rect x={112} y={42} width={90} height={74} rx={6} fill={C.white} stroke={INK} strokeWidth={3} />
          <rect x={120} y={50} width={22} height={22} rx={5} fill={C.lilac} stroke={INK} strokeWidth={2} />
          <image href="/icons/icon-192.png" x={121} y={51} width={20} height={20} />
          <Bars x={148} y={54} widths={[44, 30]} gap={9} opacity={0.35} />
          <Bars x={120} y={82} widths={[74]} opacity={0.2} />
          <Tap cx={172} cy={103} r={13} />
          <rect x={150} y={96} width={44} height={14} rx={7} fill={C.mint} stroke={INK} strokeWidth={2.5} />
        </WindowFrame>
      );
    case 'desktop-window':
      return (
        <WindowFrame title>
          <Bars x={44} y={12} widths={[60]} h={5} opacity={0.4} />
          <rect x={14} y={38} width={90} height={16} rx={3} fill={C.lavender} stroke={INK} strokeWidth={2} />
          <Bars x={14} y={64} widths={[180, 160, 130]} gap={10} />
          <rect x={14} y={100} width={56} height={26} rx={4} fill={C.mint} stroke={INK} strokeWidth={2.5} />
          <rect x={78} y={100} width={56} height={26} rx={4} fill={C.sky} stroke={INK} strokeWidth={2.5} />
        </WindowFrame>
      );
    case 'mac-menu':
      return (
        <>
          <rect x={9} y={9} width={206} height={136} rx={8} fill={C.grape} />
          <rect x={3} y={3} width={206} height={136} rx={8} fill={C.white} stroke={INK} strokeWidth={4} />
          <rect x={5} y={5} width={202} height={18} rx={5} fill={C.pale} />
          <line x1={5} x2={207} y1={23} y2={23} stroke={INK} strokeWidth={2} />
          <rect x={30} y={8} width={26} height={12} rx={3} fill={C.butter} stroke={INK} strokeWidth={2} />
          <Bars x={36} y={12} widths={[14]} h={4} opacity={0.6} />
          <Bars x={66} y={12} widths={[18, 18, 18]} gap={0} h={4} opacity={0.2} />
          <rect x={30} y={22} width={92} height={78} rx={4} fill={C.white} stroke={INK} strokeWidth={3} />
          <Bars x={38} y={30} widths={[60, 48]} gap={12} h={5} opacity={0.22} />
          <rect x={34} y={58} width={84} height={18} rx={3} fill={C.butter} stroke={INK} strokeWidth={3} />
          <Bars x={42} y={64} widths={[56]} h={5} opacity={0.55} />
          <Bars x={38} y={84} widths={[50]} h={5} opacity={0.22} />
          <Tap cx={106} cy={67} r={9} />
        </>
      );
  }
}

/** One mockup. Decorative: the step text next to it carries the meaning. */
export function GateMock({ scene, width = 96, className }: { scene: Scene; width?: number; className?: string }) {
  const id = useId().replace(/:/g, '');
  const { w, h } = SCENE_SIZE[scene];
  const pad = 12;
  return (
    <svg
      viewBox={`0 0 ${w + pad} ${h + pad - 4}`}
      width={width}
      height={Math.round((width * (h + pad - 4)) / (w + pad))}
      className={className}
      aria-hidden="true"
      focusable="false"
      data-scene={scene}
    >
      <Scene scene={scene} id={id} />
    </svg>
  );
}
